/**
 * Cancel ONE leg of an order, leaving its siblings running.
 *
 * Separate from `progressLeg` on purpose: that path is forward-only and its
 * parent-promotion rules must not learn about cancellation. Here the leg is
 * retired (including its external EAPI trip) and the parent order is rolled
 * up only when the cancellation actually settles the whole order:
 *
 *   - every leg canceled            → order canceled
 *   - the rest delivered (≥1)       → order delivered
 *   - anything still open           → order status untouched
 *
 * Sync-then-Persist: the upstream trip is cancelled BEFORE any local write,
 * so we never clear a local pointer to a trip that is still live upstream.
 * Status + audit only — no trailer moves, no yard events, no customer email.
 */
import { supabase } from "@/integrations/supabase/client";
import type { OrderStatus } from "@/types/tms";
import { listActiveLegTripsForOrder, supersedeLegTrip } from "@/lib/leg-trip-sync";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";

/** Reason written to `trips.superseded_reason` when a single leg is cancelled. */
export const LEG_CANCEL_SUPERSEDE_REASON = "leg_cancelled";

export interface CancelSiblingLeg {
  id: string;
  leg_sequence: number;
  driver_status: OrderStatus | string | null;
}

export interface ParentStatusAfterLegCancelInput {
  siblingLegs: CancelSiblingLeg[];
  legId: string;
  currentParentStatus: OrderStatus | string | null | undefined;
}

/** Pure: what the parent order should become after this leg is cancelled. */
export function computeParentStatusAfterLegCancel(
  input: ParentStatusAfterLegCancelInput,
): "canceled" | "delivered" | null {
  const { siblingLegs, legId, currentParentStatus } = input;
  const cur = currentParentStatus ?? "";
  if (cur === "canceled" || cur === "delivered") return null;
  if (siblingLegs.length === 0) return null;

  const projected = siblingLegs.map((l) =>
    l.id === legId ? "canceled" : ((l.driver_status ?? null) as string | null),
  );

  if (projected.every((s) => s === "canceled")) return "canceled";
  if (
    projected.every((s) => s === "canceled" || s === "delivered") &&
    projected.some((s) => s === "delivered")
  ) {
    return "delivered";
  }
  return null;
}

export interface CancelLegEapiClient {
  cancelTrip: (id: string, reason?: string) => Promise<unknown>;
}

export interface CancelLegInput {
  parentOrderId: string;
  legId: string;
  /** Human-readable reason label; forwarded upstream and stored on the order. */
  reason?: string | null;
  notes?: string | null;
  actorLabel: string;
  tenantId: string | null | undefined;
  eapiClient: CancelLegEapiClient | null;
  eapiReady?: boolean;
}

export interface CancelLegResult {
  legSequence: number | null;
  /** External trip id retired upstream, when the leg had one. */
  canceledTripId: string | null;
  /** New parent status, or null when the order was left as it was. */
  parentStatus: "canceled" | "delivered" | null;
}

export async function cancelLeg(input: CancelLegInput): Promise<CancelLegResult> {
  const {
    parentOrderId,
    legId,
    reason = null,
    notes = null,
    actorLabel,
    tenantId,
    eapiClient,
    eapiReady = true,
  } = input;

  if (!parentOrderId || !legId) throw new Error("Missing order or leg id");

  const { data: siblingsRaw, error: siblingsErr } = await supabase
    .from("legs")
    .select("id, leg_sequence, driver_status")
    .eq("order_id", parentOrderId)
    .order("leg_sequence", { ascending: true });
  if (siblingsErr) throw siblingsErr;
  const siblingLegs = ((siblingsRaw ?? []) as unknown) as CancelSiblingLeg[];

  const thisLeg = siblingLegs.find((l) => l.id === legId) ?? null;
  if (!thisLeg) throw new Error("This stop could not be found on the order.");
  if (thisLeg.driver_status === "canceled") {
    return { legSequence: thisLeg.leg_sequence ?? null, canceledTripId: null, parentStatus: null };
  }
  if (thisLeg.driver_status === "delivered") {
    throw new Error(
      `Leg ${thisLeg.leg_sequence} is already delivered and can no longer be cancelled.`,
    );
  }

  // ── Step 1: retire the upstream trip first ──
  const activeTrips = await listActiveLegTripsForOrder(parentOrderId);
  const trip = activeTrips.find((t) => t.leg_id === legId) ?? null;
  let canceledTripId: string | null = null;

  if (trip) {
    if (!eapiReady || !eapiClient) {
      throw new Error(
        "External trip system not ready — cannot cancel a stop with an active trip. Please retry shortly.",
      );
    }
    try {
      await eapiClient.cancelTrip(trip.eapi_trip_id, reason ?? undefined);
    } catch (err: any) {
      if (!isAlreadyCancelledUpstreamError(err)) {
        throw new Error(
          `Failed to cancel the external trip ${trip.eapi_trip_id}: ${err?.message ?? err}. The stop was NOT cancelled.`,
        );
      }
    }
    const superseded = await supersedeLegTrip(trip.trip_row_id, LEG_CANCEL_SUPERSEDE_REASON);
    if (!superseded.ok) {
      throw new Error(
        `External trip ${trip.eapi_trip_id} was cancelled, but its local record could not be updated: ${superseded.error}. Please retry or escalate.`,
      );
    }
    canceledTripId = trip.eapi_trip_id;
  }

  // ── Step 2: cancel the leg locally ──
  const { data: legRows, error: legErr } = await supabase
    .from("legs")
    .update({ driver_status: "canceled" })
    .eq("id", legId)
    .select("id");
  if (legErr) throw legErr;
  // RLS denials on UPDATE return zero rows, not an error.
  if (!legRows || legRows.length === 0) {
    throw new Error(
      "The stop was not cancelled (no matching row — likely insufficient permissions).",
    );
  }

  // ── Step 3: parent rollup ──
  const { data: parent } = await supabase
    .from("orders")
    .select("status")
    .eq("id", parentOrderId)
    .maybeSingle();
  const oldParentStatus = ((parent as any)?.status ?? null) as OrderStatus | null;

  const parentStatus = computeParentStatusAfterLegCancel({
    siblingLegs,
    legId,
    currentParentStatus: oldParentStatus,
  });

  if (parentStatus === "canceled") {
    // Routed through the RPC so the DB's cancel guard opt-in and the UPDATE
    // land on the same connection (same reason as the order-level cancel).
    const { error: rpcErr } = await supabase.rpc("cancel_order_locally", {
      _order_id: parentOrderId,
      _reason: reason,
      _notes: notes,
    });
    if (rpcErr) {
      throw new Error(
        `The stop was cancelled, but the order could not be closed: ${rpcErr.message ?? String(rpcErr)}. Please retry or escalate.`,
      );
    }
  } else if (parentStatus === "delivered") {
    const { data: orderRows, error: updErr } = await supabase
      .from("orders")
      .update({ status: "delivered", eapi_eta: null })
      .eq("id", parentOrderId)
      .select("id");
    if (updErr) throw updErr;
    if (!orderRows || orderRows.length === 0) {
      throw new Error(
        "The stop was cancelled, but the order status was not updated (likely insufficient permissions).",
      );
    }
  }

  // ── Step 4: audit ──
  if (tenantId) {
    await supabase.from("order_change_log").insert({
      order_id: parentOrderId,
      field_name: `leg_${thisLeg.leg_sequence}_driver_status`,
      old_value: (thisLeg.driver_status as string | null) ?? null,
      new_value: "canceled",
      changed_by: actorLabel,
      tenant_id: tenantId,
    } as any);

    if (parentStatus) {
      await supabase.from("order_change_log").insert({
        order_id: parentOrderId,
        field_name: "status",
        old_value: oldParentStatus,
        new_value: parentStatus,
        changed_by: actorLabel,
        tenant_id: tenantId,
      } as any);
    }
  }

  return {
    legSequence: thisLeg.leg_sequence ?? null,
    canceledTripId,
    parentStatus,
  };
}
